import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { getProfileImageUrl } from '@/lib/utils';

/**
 * MentionTextarea - A textarea with @mention dropdown support
 * 
 * Props:
 *  - value: string
 *  - onChange: (newValue: string) => void
 *  - members: [{ id, full_name, profile_image }]
 *  - placeholder: string
 *  - className: string
 *  - onKeyDown: (e) => void
 *  - rows: number
 *  - disabled: boolean
 *  - dataTestId: string
 */
const MentionTextarea = ({
  value,
  onChange,
  members = [],
  placeholder = 'Type a message...',
  className = '',
  onKeyDown,
  rows = 1,
  disabled = false,
  dataTestId = 'mention-textarea',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredMembers = members.filter((m) => {
    if (!mentionQuery) return true;
    return m.full_name?.toLowerCase().includes(mentionQuery.toLowerCase());
  }).slice(0, 8);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    onChange(newValue);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex >= 0) {
      const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || atIndex === 0) {
        const query = textBeforeCursor.slice(atIndex + 1);
        if (!query.includes(' ') || query.length < 25) {
          setMentionQuery(query);
          setMentionStart(atIndex);
          setShowDropdown(true);
          setSelectedIndex(0);
          return;
        }
      }
    }

    setShowDropdown(false);
    setMentionQuery('');
    setMentionStart(-1);
  }, [onChange]);

  const insertMention = useCallback((member) => {
    if (mentionStart < 0) return;
    const before = value.slice(0, mentionStart);
    const after = value.slice(
      mentionStart + 1 + mentionQuery.length
    );
    const newValue = `${before}@${member.full_name} ${after}`;
    onChange(newValue);
    setShowDropdown(false);
    setMentionQuery('');
    setMentionStart(-1);

    // Refocus textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = mentionStart + member.full_name.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [value, onChange, mentionStart, mentionQuery]);

  const handleKeyDown = useCallback((e) => {
    if (showDropdown && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredMembers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev === 0 ? filteredMembers.length - 1 : prev - 1
        );
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        insertMention(filteredMembers[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    }
    // Pass through to parent handler
    if (onKeyDown) onKeyDown(e);
  }, [showDropdown, filteredMembers, selectedIndex, insertMention, onKeyDown]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  };

  return (
    <div className="relative w-full">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        rows={rows}
        disabled={disabled}
        spellCheck={true}
        data-testid={dataTestId}
      />

      {showDropdown && filteredMembers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 mb-1 w-64 max-h-48 overflow-y-auto
                     bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          data-testid="mention-dropdown"
        >
          {filteredMembers.map((member, idx) => (
            <button
              key={member.id}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm
                         transition-colors ${
                           idx === selectedIndex
                             ? 'bg-purple-50 text-purple-800'
                             : 'hover:bg-gray-50 text-gray-700'
                         }`}
              onClick={() => insertMention(member)}
              data-testid={`mention-option-${member.id}`}
            >
              <Avatar className="h-6 w-6">
                <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                <AvatarFallback className="text-[10px] bg-purple-100 text-purple-700">
                  {getInitials(member.full_name)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate font-medium">{member.full_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Render text with @mentions highlighted
 */
export const MentionText = ({ content, className = '' }) => {
  if (!content) return null;

  // Match @Name patterns (captures up to 3 words after @)
  const mentionRegex = /@([A-Z][a-zA-Z]+(?:\s[A-Z][a-zA-Z]+){0,2})/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={match.index}
        className="text-purple-700 font-medium bg-purple-50 px-0.5 rounded"
      >
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return <span className={className}>{parts}</span>;
};

export default MentionTextarea;
